// The Saju book's line breaking, as the book builder does it (build-product-samples.py wrap, Book.text; specs/011 P3):
// the same glyph widths (saju-glyph-widths.json, exported from ReportLab by scripts/export_glyph_widths.py), the same
// breaking rule, the same places — so a box the Studio draws again (another person's figures, a rewritten manuscript)
// breaks where the printed book would. Everything here is measured in page points.

const PAGE_HEIGHT = 1078;
// What Python's str.split(), strip() and isspace() treat as white space — not JavaScript's \s.
const WHITE = '\\t\\n\\x0b\\x0c\\r\\x1c-\\x1f \\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const RUN = new RegExp(`[${WHITE}]+`), LEADING = new RegExp(`^[${WHITE}]+`), TRAILING = new RegExp(`[${WHITE}]+$`), SPACE = new RegExp(`^[${WHITE}]$`);

/** A text as Book.text prints it: its dashes and minus sign as the fonts have them, a no-break space as a space. */
export function asPrinted(text) {
  return String(text).replaceAll('–', '~').replaceAll('—', ' · ').replaceAll('−', '-').replaceAll(' ', ' ');
}

/** The measure of the book's three faces (body, bold, serif) from the exported tables ({ faces: { key: { default, runs } } }). */
export function createPrintMetrics(tables) {
  const faces = {};
  for (const [key, face] of Object.entries(tables.faces)) {
    const widths = new Map();
    for (const [first, last, width] of face.runs) for (let code = first; code <= last; code++) widths.set(code, width);
    faces[key] = { widths, default: face.default };
  }
  const faceOf = font => faces[font] ?? (() => { throw new Error(`책의 글꼴이 아닙니다: ${font}`); })();

  /** ReportLab's width: 0.001 × size × the sum of the glyph widths (whole numbers), a missing glyph as the face's default. */
  function stringWidth(text, font, size) {
    const face = faceOf(font);
    let sum = 0;
    for (const character of text) sum += face.widths.get(character.codePointAt(0)) ?? face.default;
    return 0.001 * size * sum;
  }

  /** The builder's wrap(): words while they fit the width, a word that cannot fit a line of its own broken by character. */
  function wrap(text, size, width, font) {
    const lines = [];
    for (const paragraph of String(text).split('\n')) {
      let line = '';
      for (const word of paragraph.split(RUN).filter(Boolean)) {
        const candidate = `${line} ${word}`.replace(LEADING, '').replace(TRAILING, '');
        if (stringWidth(candidate, font, size) <= width) { line = candidate; continue; }
        if (line) { lines.push(line); line = ''; }
        for (const character of word) {
          if (line && stringWidth(line + character, font, size) > width) { lines.push(line); line = character; }
          else line += character;
        }
      }
      lines.push(line.replace(TRAILING, ''));
    }
    return lines;
  }

  /** The characters a face has no glyph for (Book.text refuses a text with any but white space). */
  function unsupported(text, font) {
    const face = faceOf(font);
    return [...new Set([...text].filter(character => !SPACE.test(character) && !face.widths.has(character.codePointAt(0))))];
  }

  /** Where a printed line starts: centred in its box (as Book.text centres it) or at the box's left. */
  const lineX = (line, { x, width, size, font, center }) => center ? x + (width - stringWidth(line, font, size)) / 2 : x;

  /** Book.text's budget: a box with a height refuses lines that need more than it has (a tenth of a point of slack). */
  const fits = (lineCount, size, height) => height === null || !(lineCount * (size * 1.43) > height + 0.1);

  /**
   * A box's printed lines as the canvas export records them (canvas_scenes.py SceneRecorder.text): the text, where it
   * starts, its measured width plus half a point, and the line's height; an empty line takes its place but draws nothing.
   * The y goes through the PDF's own coordinates as the builder's does, so it is the same number.
   */
  function printedLines(box, text) {
    const leading = box.size * 1.43;
    const lines = [];
    wrap(text, box.size, box.width, box.font).forEach((line, index) => {
      if (!line) return;
      const pdfY = PAGE_HEIGHT - box.y - box.size - index * leading;
      lines.push({ text: line, x: lineX(line, box), y: PAGE_HEIGHT - pdfY - box.size, width: stringWidth(line, box.font, box.size) + 0.5, height: box.size * 1.43 });
    });
    return lines;
  }

  /**
   * The page title's type size (editorial-product-renderer.py Publication.page): from 30 (a compact page), 68 (a native
   * picture page) or 38, a point smaller while the title needs more than its height, down to 28. Measured on the title as
   * given, before Book.text prints its dashes.
   */
  function pageTitleSize(title, { compact = false, native = false } = {}) {
    let size = compact ? 30 : native ? 68 : 38;
    const maxHeight = compact ? 78 : 116;
    while (wrap(title, size, 650, 'bold').length * size * 1.43 > maxHeight && size > 27) size -= 1;
    return size;
  }

  /** Where a summary's text starts (Publication.summary): centred in its frame by the lines it needs, 6 points down. */
  function summaryY(text, { y, h, size, topic }) {
    const used = wrap(text, size, topic === 'personality' ? 452 : 532, 'serif').length * size * 1.43;
    return y + (h - used) / 2 + 6;
  }

  return Object.freeze({ stringWidth, wrap, unsupported, lineX, fits, printedLines, pageTitleSize, summaryY });
}

let metrics = null, loading = null;
/** The metrics, read once from saju-glyph-widths.json beside this module (a file in Node, a fetch in the browser). */
export function loadPrintMetrics() {
  if (metrics) return Promise.resolve(metrics);
  loading ??= (async () => {
    const url = new URL('./saju-glyph-widths.json', import.meta.url);
    let tables;
    if (url.protocol === 'file:') {
      const [{ readFile }, { fileURLToPath }] = await Promise.all([import('node:fs/promises'), import('node:url')]);
      tables = JSON.parse(await readFile(fileURLToPath(url), 'utf8'));
    } else {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`인쇄 글자 폭 표를 불러오지 못했습니다 (${response.status}).`);
      tables = await response.json();
    }
    metrics = createPrintMetrics(tables);
    return metrics;
  })().catch(reason => { loading = null; throw reason; });
  return loading;
}

/** The metrics once loadPrintMetrics has read them, else null (a component draws synchronously). */
export const printMetrics = () => metrics;
